// Tipos e regras de cálculo do TrajetoFácil. Módulo seguro para o navegador.

export type VehicleType = "carro" | "moto" | "onibus" | "caminhao";

export const VEHICLES: { value: VehicleType; label: string; axles: boolean; defaultKmL: number }[] = [
  { value: "carro", label: "Carro", axles: false, defaultKmL: 10 },
  { value: "moto", label: "Moto", axles: false, defaultKmL: 30 },
  { value: "onibus", label: "Ônibus", axles: true, defaultKmL: 3 },
  { value: "caminhao", label: "Caminhão", axles: true, defaultKmL: 2.5 },
];

/**
 * Multiplicador de pedágio sobre a tarifa base (carro de passeio, 1 eixo cobrado).
 * Segue a lógica das concessionárias brasileiras: tarifa por eixo.
 * Moto costuma ser isenta ou pagar 50% — usamos 0,5 como estimativa conservadora.
 */
export function tollMultiplier(vehicle: VehicleType, axles: number): number {
  switch (vehicle) {
    case "moto":
      return 0.5;
    case "carro":
      return 1;
    case "onibus":
      return Math.max(2, axles);
    case "caminhao":
      return Math.max(2, axles);
  }
}

/** Uma praça (ou trecho free flow) cobrada no percurso. */
export type TollPlazaHit = {
  code: string;
  name: string;
  ref: string | null;
  uf: string | null;
  concessionaire: string | null;
  priceBrl: number;
  atKm: number;
};

export type Leg = {
  distanceKm: number;
  durationSec: number;
  /** Pedágio base devolvido pelo Google (categoria carro), em BRL. null = indisponível */
  baseTollBrl: number | null;
  polyline: string | null;
  /**
   * Pedágio já na categoria oficial do veículo escolhido, somando as praças
   * reais do trajeto. Quando presente, não recebe multiplicador.
   */
  tollBrl?: number | null;
  /** Praças cobradas, para o usuário auditar praça por praça */
  tollPlazas?: TollPlazaHit[];
};

export type RouteResult = {
  outbound: Leg;
  inbound: Leg | null;
  originLabel: string;
  /** Coordenadas resolvidas (modo de testes gratuito) para marcar no mapa */
  originCoord?: { lat: number; lng: number } | null;
  destinationCoord?: { lat: number; lng: number } | null;
  /** true = resultado reaproveitado do nosso banco (pesquisa com menos de 30 dias) */
  cached?: boolean;
  /** data da pesquisa original (ISO) */
  searchedAt?: string;
  /** vigência das tarifas usadas (ISO date) */
  tariffVersion?: string | null;
  destinationLabel: string;
};

export type CostBreakdown = {
  distanceKm: number;
  durationSec: number;
  fuelBrl: number;
  tollBrl: number | null;
  totalBrl: number;
};

export function legCost(
  leg: Leg,
  opts: {
    pricePerLiter: number;
    kmPerLiter: number;
    vehicle: VehicleType;
    axles: number;
    manualToll: number | null;
    /** Valor por km direto (R$/km). Usado quando não há preço/litro nem km/litro,
     *  apenas o multiplicador de repasse como tarifa por km. */
    perKmRate?: number;
  },
): CostBreakdown {
  const fuelBrl =
    opts.perKmRate && opts.perKmRate > 0
      ? leg.distanceKm * opts.perKmRate
      : opts.kmPerLiter > 0
        ? (leg.distanceKm / opts.kmPerLiter) * opts.pricePerLiter
        : 0;
  const toll =
    // Tarifa oficial da categoria: valor final, sem multiplicador.
    leg.tollBrl !== undefined && leg.tollBrl !== null
      ? leg.tollBrl
      : leg.baseTollBrl !== null
        ? leg.baseTollBrl * tollMultiplier(opts.vehicle, opts.axles)
        : opts.manualToll !== null
          ? opts.manualToll
          : null;
  return {
    distanceKm: leg.distanceKm,
    durationSec: leg.durationSec,
    fuelBrl,
    tollBrl: toll,
    totalBrl: fuelBrl + (toll ?? 0),
  };
}

export function formatBrl(value: number): string {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function formatKm(value: number): string {
  return `${value.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} km`;
}

export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  if (h === 0) return `${m} min`;
  return `${h}h${String(m).padStart(2, "0")}`;
}
